import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { type AddArtifactRequest, type Artifact } from '@bliks-io/contracts';
import {
  ArtifactCard,
  EntityPickerModal,
  EntityPickerHighlightedText,
  type EntityPickerConfig,
  type EntityPickerRowMeta,
  type EntityPickerSearchKey
} from '@bliks-io/ui';
import { useArtifacts } from '@/api/queries/useArtifacts';
import { useCreateArtifact } from '@/api/mutations/useArtifacts';
import { useArtifactPickerSuggestions } from '@/api/queries/usePickerSuggestions';
import { ArtifactFormDialog } from '@/pages/organization/artifacts/components/ArtifactFormDialog';
import { buildEntityPickerCopy, buildSuggestNew } from './buildCopy';
import { useCreatedIdsMerge } from './hooks/useCreatedIdsMerge';
import { EntityPickerBucketsList } from './shared/EntityPickerBucketsList';
import {
  buildBucketSections,
  buildRowMetaIndex,
  shouldShowBucketView
} from './shared/buildBucketSections';

const SEARCH_KEYS: EntityPickerSearchKey<Artifact>[] = [
  { name: 'name', weight: 0.5 },
  { name: 'description', weight: 0.3 },
  { name: 'type', weight: 0.2 }
];

export interface ArtifactPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'single' | 'multi';
  selectedIds: string[];
  onConfirm: (ids: string[]) => void;
  excludeIds?: string[];
  onBack?: () => void;
  processId?: string;
  recordingId?: string;
}

export function ArtifactPickerModal({
  open,
  onOpenChange,
  mode,
  selectedIds,
  onConfirm,
  excludeIds,
  onBack,
  processId,
  recordingId
}: ArtifactPickerModalProps) {
  const { t } = useTranslation();
  const { data: artifacts = [], isLoading } = useArtifacts();
  const { data: suggestions, isLoading: isLoadingSuggestions } = useArtifactPickerSuggestions({
    processId,
    recordingId,
    enabled: open
  });
  const createMutation = useCreateArtifact();
  const [createOpen, setCreateOpen] = useState(false);
  const [createInitialName, setCreateInitialName] = useState('');
  const { mergedSelectedIds, registerCreated } = useCreatedIdsMerge(selectedIds, open);

  const artifactList = artifacts as Artifact[];
  const byId = useMemo(() => new Map(artifactList.map((a) => [a._id, a] as const)), [artifactList]);

  const rowMetaById = useMemo(() => buildRowMetaIndex(suggestions), [suggestions]);

  const config = useMemo<EntityPickerConfig<Artifact>>(() => {
    const baseCopy = buildEntityPickerCopy(t, 'artifact');
    return {
      headerIcon: <FileText className="h-4 w-4" />,
      searchKeys: SEARCH_KEYS,
      matchInField: {
        primaryField: 'name',
        fieldLabels: {
          description: t('entityPicker.common.fields.description'),
          type: t('entityPicker.common.fields.type')
        }
      },
      facet: { field: 'type', allLabel: t('entityPicker.common.allFacet') },
      getRowMeta: (item): EntityPickerRowMeta => rowMetaById.get(item._id) ?? {},
      getChipName: (item) => item.name ?? '',
      renderRow: (item, ctx) => (
        <ArtifactCard
          artifact={item}
          variant="with-type"
          itemVariant="default"
          size="xs"
          titleSlot={
            <EntityPickerHighlightedText
              text={item.name ?? ''}
              field="name"
              matches={ctx.matches}
            />
          }
        />
      ),
      bucketing: {
        isLoading: isLoadingSuggestions,
        hasBuckets: (candidates) => shouldShowBucketView(suggestions, candidates),
        renderEmptyQueryView: ({ items, selectedIds: draft, toggleId }) => {
          const subset = new Map(items.map((a) => [a._id, a] as const));
          const filteredById = new Map<string, Artifact>();
          for (const [id, a] of subset) if (byId.has(id)) filteredById.set(id, a);
          const sections = buildBucketSections<Artifact>({
            byId: filteredById,
            suggestions,
            t,
            config: {
              byProcessTitle: (processName) =>
                processName
                  ? `${t('entityPicker.artifact.buckets.byProcess')} · ${processName}`
                  : t('entityPicker.artifact.buckets.byProcess'),
              byProcessSubtitle: (sampleSize) =>
                t('entityPicker.system.buckets.byProcessSampleSize', {
                  count: sampleSize
                }),
              byTypeTitle: (type) => t('entityPicker.artifact.buckets.byType', { type })
            }
          });
          return (
            <EntityPickerBucketsList<Artifact>
              sections={sections}
              selectedIds={draft}
              onToggle={toggleId}
              mode={mode}
              renderItem={(item) => (
                <ArtifactCard artifact={item} variant="with-type" itemVariant="default" size="xs" />
              )}
            />
          );
        }
      },
      copy: baseCopy
    };
  }, [t, suggestions, isLoadingSuggestions, byId, rowMetaById, mode]);

  const suggestNew = buildSuggestNew((q) => {
    setCreateInitialName(q);
    setCreateOpen(true);
  });

  const handleCreate = async (data: AddArtifactRequest) => {
    const result = await createMutation.mutateAsync(data);
    setCreateOpen(false);
    if (result.data?._id) {
      registerCreated(result.data._id);
    }
  };

  return (
    <>
      <EntityPickerModal<Artifact>
        open={open}
        onOpenChange={onOpenChange}
        items={artifactList}
        isLoading={isLoading}
        selectedIds={mergedSelectedIds}
        mode={mode}
        onConfirm={onConfirm}
        config={config}
        suggestNew={suggestNew}
        onBack={onBack}
        excludeIds={excludeIds}
      />
      <ArtifactFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={(data) => handleCreate(data as AddArtifactRequest)}
        initialName={createInitialName}
      />
    </>
  );
}
